// Where the console's "needs your input" items go (client/src/pages/oversight-console.tsx):
// each open escalation sits under the message it came from, and the conversation
// list carries a count — no wall of yellow above the conversations. Pure, so the
// client tests hold the rules without a DOM.

export interface EscalationLike {
  id: number;
  conversationId: number;
  messageId: number | null;
  type: string;
}

const LABELS: Record<string, string> = {
  question: "Question",
  reviewed_edit: "Edit to reviewed order",
  synced_edit: "Edit to sent order",
  unknown_account: "Unknown number",
  parse_error: "Couldn't parse",
  order_create_failed: "Order failed",
  product_match: "Unrecognized items", // a held order, resolved through the card
};

export function escalationLabel(type: string): string {
  return LABELS[type] ?? type;
}

/**
 * The strip's heading (Jon, 2026-10-22). A question Clyde asked in the group
 * thread (the row's `threadQuestion`) waits on the buyer — anyone in the thread
 * can answer it, so it "needs input". Only a question put to you alone (a hold,
 * a supervisor question) "needs your input".
 */
export function needsInputHeading(escalation: { payload?: unknown }): string {
  const asked = !!(escalation.payload as { threadQuestion?: unknown } | null | undefined)?.threadQuestion;
  return asked ? "Needs input" : "Needs your input";
}

/**
 * The rows that are to-dos here. A rep check (Clyde ran his guesses past the rep
 * 1:1) and an orientation (his questions to the rep when a thread starts) are the
 * transport's record of what it asked, answered by the rep's text.
 */
export function consoleEscalations<T extends EscalationLike>(rows: T[]): T[] {
  return rows.filter((e) => e.type !== "rep_check" && e.type !== "orientation");
}

/** Open items per conversation, for the list's badge. */
export function openCountByConversation(rows: EscalationLike[]): Map<number, number> {
  const counts = new Map<number, number>();
  for (const e of rows) counts.set(e.conversationId, (counts.get(e.conversationId) ?? 0) + 1);
  return counts;
}

/**
 * One conversation's items, placed: under the message each came from when that
 * message is on screen, else at the top of the thread (no message on file, or one
 * older than the bounded read) — never dropped.
 */
export function placeEscalations<T extends EscalationLike>(
  rows: T[],
  conversationId: number,
  messageIds: number[],
): { byMessage: Map<number, T[]>; unplaced: T[] } {
  const onScreen = new Set(messageIds);
  const byMessage = new Map<number, T[]>();
  const unplaced: T[] = [];
  for (const e of rows) {
    if (e.conversationId !== conversationId) continue;
    if (e.messageId != null && onScreen.has(e.messageId)) {
      byMessage.set(e.messageId, [...(byMessage.get(e.messageId) ?? []), e]);
    } else {
      unplaced.push(e);
    }
  }
  return { byMessage, unplaced };
}

/** The one line above the conversations: "2 conversations need input". */
export function needsInputLine(conversationCount: number): string {
  return `${conversationCount} conversation${conversationCount === 1 ? "" : "s"} need${conversationCount === 1 ? "s" : ""} input`;
}

/** Where the resolve card's date came from (GET /agent/escalations/:id/suggested-date). */
export function suggestedDateNote(s: { source: string | null; quote?: string } | null | undefined): string | null {
  if (!s?.source) return null;
  if (s.source === "held") return "The day they asked for";
  if (s.source === "thread") return s.quote ? `From the thread: “${s.quote}”` : "From the thread";
  if (s.source === "next_delivery") return "Next delivery day";
  return null;
}
